import { useCallback, useMemo } from "react";
import { debug as debugFn } from "debug";
import { errAsync, ResultAsync } from "neverthrow";
import { useTranslation } from "react-i18next";
import { Navigate, useNavigate, useParams } from "react-router-dom";

import type {
  IncidentWorkItemWithContext,
  WorkflowVersion,
} from "@thalamos/vision-common";
import {
  FormSectionSchema,
  IncidentFormTypeSchema,
  IncidentWorkItemIdSchema,
  workflows,
} from "@thalamos/vision-common";

import api, { apiHooks } from "../../api/index.js";
import { useUser } from "../../Context/UserContext.js";
import type { McaPersonDetailsFormProps } from "../../forms/McaPersonDetails/McaPersonDetailsForm.js";
import {
  McaPersonDetailsForm,
  McaPersonDetailsFormSkeleton,
} from "../../forms/McaPersonDetails/McaPersonDetailsForm.js";
import { useToastNotifications } from "../../hooks/useToastNotifications.js";
import { createRoute } from "../../routing/createRoute.js";
import { Error as ErrorComponent } from "../Error/Error.js";

const debug = debugFn("vision-frontend:McaPersonDetailsPage");

export type McaPersonDetailsPageProps = Record<string, unknown>;

// This component is responsible for managing the API calls and passing
// the data to the inner component.
export function McaPersonDetailsPage(_props: McaPersonDetailsPageProps) {
  const params = useParams();

  const incidentWorkItemId = IncidentWorkItemIdSchema.safeParse(params.id);
  if (!incidentWorkItemId.success) {
    throw new Error("Invalid incident work item id");
  }

  const [
    { data: incidentWorkItem, error: incidentWorkItemError },
    refetchWorkItem,
  ] = apiHooks.incidentWorkItem.get(incidentWorkItemId.data);

  const { user } = useUser();

  if (incidentWorkItemError || user === undefined) {
    return <ErrorComponent />;
  }

  return (
    <McaPersonDetailsPageInner
      incidentWorkItem={incidentWorkItem ?? undefined}
      refetchWorkItem={refetchWorkItem}
    />
  );
}

export type McaPersonDetailsPageInnerProps = {
  incidentWorkItem?: IncidentWorkItemWithContext;
  refetchWorkItem: ReturnType<typeof apiHooks.incidentWorkItem.get>[1];
};

export function McaPersonDetailsPageInner({
  incidentWorkItem,
  refetchWorkItem,
}: McaPersonDetailsPageInnerProps) {
  if (incidentWorkItem === undefined) {
    return <McaPersonDetailsPageInnerLoading />;
  }

  const hasMcaForm = incidentWorkItem.forms.some(
    (form) => form.formType === IncidentFormTypeSchema.enum.mentalCapacityAct,
  );
  if (!hasMcaForm) {
    return (
      <Navigate
        replace
        to={createRoute.incidentProgressPage(
          incidentWorkItem.incidentWorkItem.id,
        )}
      />
    );
  }

  return (
    <McaPersonDetailsPageInnerLoaded
      incidentWorkItem={incidentWorkItem}
      refetchWorkItem={refetchWorkItem}
    />
  );
}

function McaPersonDetailsPageInnerLoading() {
  return <McaPersonDetailsFormSkeleton />;
}

export function McaPersonDetailsPageInnerLoaded({
  incidentWorkItem,
  refetchWorkItem,
}: Required<McaPersonDetailsPageInnerProps>) {
  const navigate = useNavigate();
  const toastNotifications = useToastNotifications();
  const { t } = useTranslation();

  const incidentWorkItemId = incidentWorkItem.incidentWorkItem.id;
  const workflowVersion = incidentWorkItem.incidentWorkItem
    .workflowVersion as WorkflowVersion;

  const mcaForm = useMemo(
    () =>
      incidentWorkItem.forms.find(
        (form) =>
          form.formType === IncidentFormTypeSchema.enum.mentalCapacityAct,
      ),
    [incidentWorkItem.forms],
  );
  if (!mcaForm) {
    throw new Error("Mental Capacity Act form not found");
  }

  const mcaFormId = mcaForm.id;

  // Get the form specification (including pre-computed defaultValues)
  const formSpecification = useMemo(() => {
    const existingData =
      mcaForm.sections.find(
        (section) => section.id === FormSectionSchema.enum.mcaPersonDetails,
      )?.data ?? {};
    return workflows[workflowVersion]?.[
      "mentalCapacityAct"
    ].formDefinition.sections
      .find((section) => section.id === FormSectionSchema.enum.mcaPersonDetails)
      ?.buildFormSpecification({ incidentWorkItem, existingData });
  }, [workflowVersion, incidentWorkItem, mcaForm.sections]);

  if (!formSpecification) {
    throw new Error("Form specification not found");
  }

  const { formSchema: schema, defaultValues: initialValues } =
    formSpecification;

  const existingValuesWereValid = useMemo(() => {
    const existingData =
      mcaForm.sections.find(
        (section) => section.id === FormSectionSchema.enum.mcaPersonDetails,
      )?.data ?? {};
    return schema.safeParse(existingData).success;
  }, [schema, mcaForm.sections]);

  const enableAutoSave = useMemo(
    () =>
      workflows[workflowVersion]?.[
        "mentalCapacityAct"
      ].formDefinition.sections.find(
        (section) => section.id === FormSectionSchema.enum.mcaPersonDetails,
      )?.autoSave ?? false,
    [workflowVersion],
  );

  const onAutoSave: Exclude<
    McaPersonDetailsFormProps<typeof workflowVersion>["onAutoSave"],
    undefined
  > = useCallback(
    (values) => {
      if (existingValuesWereValid) {
        // Because we started off with a valid and complete form, we should not regress
        // to a partial form.
        const isValid = schema.safeParse(values).success;
        if (!isValid) {
          return errAsync("Blocked auto-save: form is not valid");
        }
      }
      return ResultAsync.fromPromise(
        api.incidentWorkItem.updateData(
          incidentWorkItemId,
          mcaFormId,
          FormSectionSchema.enum.mcaPersonDetails,
          values,
          existingValuesWereValid,
        ),
        (error) =>
          error instanceof Error ? error.message : JSON.stringify(error),
      )
        .orTee(debug)
        .map(() => true);
    },
    [incidentWorkItemId, existingValuesWereValid, schema, mcaFormId],
  );

  const onSubmit: McaPersonDetailsFormProps<
    typeof workflowVersion
  >["onSubmit"] = useCallback(
    async (values: Record<string, unknown>) => {
      try {
        const response = await api.incidentWorkItem.updateData(
          incidentWorkItemId,
          mcaFormId,
          FormSectionSchema.enum.mcaPersonDetails,
          values,
          true,
        );
        if (response.status === 204) {
          await refetchWorkItem();
          navigate(`/incident/${incidentWorkItemId}/section-complete`, {
            state: { type: FormSectionSchema.enum.mcaPersonDetails },
          });
          return;
        } else {
          throw new Error(JSON.stringify(response.data.message));
        }
      } catch (error) {
        debug("Failed to update MCA Person Details section", error);
        toastNotifications.show(
          t("incidentWorkItem.notificationFailedToSave"),
          {
            severity: "error",
          },
        );
      }
    },
    [
      incidentWorkItemId,
      navigate,
      refetchWorkItem,
      mcaFormId,
      t,
      toastNotifications,
    ],
  );

  const onCancel: McaPersonDetailsFormProps<
    typeof workflowVersion
  >["onCancel"] = useCallback(() => {
    navigate(createRoute.incidentProgressPage(incidentWorkItemId));
  }, [incidentWorkItemId, navigate]);

  return (
    <McaPersonDetailsForm
      onAutoSave={enableAutoSave ? onAutoSave : undefined}
      onSubmit={onSubmit}
      onCancel={onCancel}
      initialValues={initialValues}
      incidentWorkItem={incidentWorkItem}
      workflowVersion={workflowVersion}
    />
  );
}
